<template>
	<div class="flex items-center space-x-3">
        <div v-show="showSearch" class="relative flex items-center" title="Search">
        	<div
        		@click="isSearchOpen = ! isSearchOpen"
        		:class="{ 'pl-3 absolute left-0 z-40': isSearchOpen }"
        	>
        		<inline-svg name="search"></inline-svg>
        	</div>
            <div v-show="isSearchOpen" class="flex items-center relative">
                <input @input="debounce" type="text" ref="search" class="pl-10 form-input is_search" placeholder="Search" autofocus>
                <div @click="resetSearch">
                	<inline-svg name="x" classes="absolute bg-gray-500 cursor-pointer fill-current h-4 mr-2 right-0 rounded-full text-white w-4"></inline-svg>
                </div>
            </div>
        </div>
        <div v-if="showViewOptions || showMapViewOption" class="flex items-center">
			<div class="bg-gray-100 p-0.5 rounded-lg items-center sm:flex">
				<button
					v-if="showViewOptions"
					@click="setView('grid')"
					type="button"
					:class="{ 'ml-0.5 bg-white': selectedViewOption == 'grid' }"
					class="p-1.5 rounded-md shadow-sm text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
						<inline-svg name="grid"></inline-svg>
				</button>
				<button
					v-if="showViewOptions"
					@click="setView('table')"
					type="button"
					:class="{ 'ml-0.5 bg-white': selectedViewOption == 'table' }"
					class="p-1.5 rounded-md text-gray-400 hover:bg-white hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
						<inline-svg name="table"></inline-svg>
				</button>
				<button
					v-if="showMapViewOption"
					@click="setView('map')"
					type="button"
					:class="{ 'ml-0.5 bg-white': selectedViewOption == 'map' }"
					class="p-1.5 rounded-md text-gray-400 hover:bg-white hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
						<inline-svg name="map"></inline-svg>
				</button>
			</div>
		</div>

        <div v-if="showFilters" class="mr-4" title="Filters">
            <v-popover offset="10">
                <button>
                    <inline-svg name="filter"></inline-svg>
                </button>

                <template slot="popover">
                    <div>
                        <div class="mb-4 p-6">
                        	<div class="mb-5 border-dotted border-b-2 border-gray-light">
			                    <div v-if="showClientFilter && $gate.isAdmin()" class="mb-4 flex items-center">
			                        <span class="mr-6">Client</span>
			                        <select v-model="selectedClientId" class="ml-2 form-select max-w-fit">
			                            <option value="">All</option>
			                            <option v-for="client in allClients" :key="client.id" :value="client.id">{{ client.name }}</option>
			                        </select>
			                    </div>
		                        <div v-if="rules[dataType]['sortOptions']" class="flex items-center mb-4">
		                            <span class="mr-6">Sort by</span>
		                            <select v-model="sortDataBy" class="form-select max-w-fit">
		                                <option v-if="showClientFilter && $gate.isAdmin()" value="client.name">Client</option>
		                                <option v-for="(item, itemIndex) in rules[dataType]['sortOptions']" :key="itemIndex" :value="item.key" v-text="item.text"></option>
		                            </select>
		                            <div class="ml-2 pt-1" title="Toggle Sort Order" @click="sortDirection = ! sortDirection">
		                                <inline-svg name="shuffle"></inline-svg>
		                            </div>
		                        </div>
		                    </div>
		                    <div v-if="filters" class="custom-filters">
                        		<slot name="filters"></slot>
                        	</div>
                        </div>
                        
                        <div class="flex justify-center m-2">
                            <button class="btn btn-small btn-blue" v-close-popover>Close</button>
                        </div>
                    </div>
                </template>
            </v-popover>
        </div>

		<portal v-if="! loading" :to="portal">
			<slot name="no-search-results-message">
	            <div v-if="$store.state[dataType].filtered.length == 0" class="flex items-center justify-center">
	                <p class="none-found">No results found, please change search criteria</p>
	            </div>
			</slot>
		</portal>

		<portal v-if="loading" to="loading">
			<slot name="loading-svg">
				<inline-svg name="loading"></inline-svg>
			</slot>
		</portal>
	</div>
</template>

<script>
	import moment from 'moment';
	import { mapState } from 'vuex';

	export default {
	    name: 'ReoptFilters',

        props: {
            dataType: {
            	type: String,
            	required: true
            },

            filters: {
				type: Object
            },

            showSearch: {
				type: Boolean,
				default: true
            },

            openSearch: {
				type: Boolean,
				default: false
            },

            showViewOptions: {
				type: Boolean,
				default: true
            },

            selectedViewOption: {
            	type: String,
            	required: false
            },

            showMapViewOption: {
				type: Boolean,
				default: false
            },

            showFilters: {
				type: Boolean,
				default: true
            },

            showClientFilter: {
				type: Boolean,
				default: false
            },

            sortBy: {
            	type: String
            },

            sortAsc: {
            	type: Boolean,
            	default: true,
            },

            portal: {
            	type: String,
            	default: 'no-search-results'
            }
        },

		data() {
			return {
				allClients: [],
				selectedClientId: '',
				criteria: '',
				layout: this.selectedViewOption,
                isSearchOpen: this.openSearch,
                sortDataBy: this.sortBy,
                sortDirection: this.sortAsc,
                loading: false
			}
		},

		created() {
			this.$parent.$on('filterRequest', () => {
				this.filter();
			});
		},

		mounted() {
			this.filter();

			// pull clients for client filter
			if (this.showClientFilter && this.$gate.isAdmin()) {			
	            axios.get('/api/clients').then((response) => {
					this.allClients = _.map(response.data, (client) => {
						return {
							id: client.id,
							name: client.name
						}
					});
				}).catch((error) => {
					console.log(error);
	           	});
			}
		},

		watch: {
	        criteria: function () {
				this.search();

				this.$emit('searching', true);
        	},

            filters: function () {
            	this.filter();
            },

            selectedClientId: function () {
            	this.filter();
            },

            sortDataBy: function () {
                this.sort();
            },

            sortDirection: function () {
                this.sort();
            },
        },

        computed: {
        	...mapState(['clients']),
        	...mapState(['subsidiaries']),
        	...mapState(['users']),
        	...mapState(['buildings']),
        	...mapState(['sites']),
        	...mapState(['reatlasSites']),
        	...mapState(['documents']),
        	...mapState(['reports']),
        	...mapState(['contacts']),
        	...mapState(['projects']),
        	...mapState(['projectComps']),        	
        	...mapState(['projectSites']),
        	...mapState(['changeLog']),

        	rules() {
        		return {
		            clients: {
        				search: async () => {
			                this.clients.filtered = this.clients.all.filter(client => {
			                    return client.name.toLowerCase().includes(this.criteria.toLowerCase())
			                });
        				},

		            	filter: () => {
        					this.rules['clients']['search']();
						},

						sortOptions: [
							{
								key: 'name',
								text: 'Name'
							},
						]
		            },

		            subsidiaries: {
        				search: async () => {
			                this.subsidiaries.filtered = this.subsidiaries.all.filter(subsidiary => {
			                    return subsidiary.name.toLowerCase().includes(this.criteria.toLowerCase())
			                    	|| subsidiary.address.toLowerCase().includes(this.criteria.toLowerCase())
			                    	|| subsidiary.city.toLowerCase().includes(this.criteria.toLowerCase())
			                    	|| subsidiary.state.toLowerCase().includes(this.criteria.toLowerCase())
			                    	|| subsidiary.postal_code.toLowerCase().includes(this.criteria.toLowerCase())
			                });
        				},

		            	filter: () => {
        					this.rules['subsidiaries']['search']();
						},

						sortOptions: [
							{
								key: 'name',
								text: 'Name'
							},
						]
		            },

		            users: {
        				search: async () => {
			                this.users.filtered = this.users.all.filter(user => {
			                    return ((user?.name?.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (user?.role?.name != null && user.role.name.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (user?.title?.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (user?.email?.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (user?.cell?.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (user?.phone?.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (user?.extension?.toLowerCase().includes(this.criteria.toLowerCase())))
			                });
        				},

		            	filter: () => {
			                this.rules['users']['search']();
						},

						sortOptions: [
							{
								key: 'name',
								text: 'Name'
							},
							{
								key: 'email',
								text: 'Email'
							},
							{
								key: 'role.name',
								text: 'Role'
							},
						]
		            },

        			buildings: {
        				search: async () => {
        					this.loading = true;

        					this.buildings.filtered = [];

        					if (this.criteria == '') {
        						this.loading = false;
        						return;
        					}

							this.$store.dispatch('loadBuildings', this.criteria)
								.takeAtLeast(300)
								.then(() => {
									this.rules[this.dataType]['filter']()
									this.sort();
									this.loading = false;
								});
        				},
        				
        				filter: () => {
			                this.buildings.filtered = this.buildings.filtered.filter(building => {
			                    return (
			                    		this.filters.availableSqFtFilter == '' ||
			                   				building.available_square_footage >= parseInt(this.filters.availableSqFtFilter)
									   )
			                   		&& (! this.filters.forSaleFilter || (this.filters.forSaleFilter && building.for_sale))
			                });
						},

						sortOptions: [
							{
								key: 'address_line_1',
								text: 'Address'
							},
							{
								key: 'total_square_footage',
								text: 'Square Feet'
							},
							{
								key: 'available_square_footage',
								text: 'Available Square Feet'
							},
						]
		            },

        			reatlasSites: {
        				search: async () => {
			                this.reatlasSites.filtered = this.reatlasSites.filtered.filter(site => {
			                    return (
			                    		site.building.address_line_1.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.city.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.state.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.postal_code.toLowerCase().includes(this.criteria.toLowerCase()))
			                });
        				},

        				filter: () => {
        					this.rules['reatlasSites']['search']();

			                this.reatlasSites.filtered = this.reatlasSites.filtered.filter(site => {
			                    return (this.filters.showArchivedSites
			                    		|| (! this.filters.showArchivedSites && site.archived_at == null)
			                    	   ) && (this.selectedClientId == '' || site.client_id == this.selectedClientId);
			                });
						},

						sortOptions: [
							{
								key: 'building.address_line_1',
								text: 'Address'
							},
							{
								key: 'lease_end_date',
								text: 'Lease End Date'
							},
							{
								key: 'utilization',
								text: 'Utilization'
							},
							{
								key: 'estimated_waste',
								text: 'Estimated Waste'
							},
						]
		            },

        			sites: {
        				search: async () => {
			                this.sites.filtered = this.sites.filtered.filter(site => {
			                    return (
			                    		site.building.address_line_1?.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.city?.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.state?.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.postal_code?.toLowerCase().includes(this.criteria.toLowerCase()))
			                });

			                this.loading = false;
        				},

        				filter: () => {
        					this.rules['sites']['search']();

			                this.sites.filtered = this.sites.filtered.filter(site => {
			                    return (this.filters.showArchivedSites
			                    		|| (! this.filters.showArchivedSites && site.archived_at == null)
			                    	   ) && (this.selectedClientId == '' || site.client_id == this.selectedClientId);
			                });
						},

						sortOptions: [
							{
								key: 'building.address_line_1',
								text: 'Address'
							},
							{
								key: 'lease_end_date',
								text: 'Lease End Date'
							},
							{
								key: 'utilization',
								text: 'Utilization'
							},
							{
								key: 'estimated_waste',
								text: 'Estimated Waste'
							},
						]
		            },

        			projects: {
        				search: async () => {
			                this.projects.filtered = this.projects.filtered.filter(project => {
			                    return (
			                    		project.name.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  project.project_type.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  project.priority.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  project.status.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  project.stage.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  project.property_type.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  project.property_usage.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  project.property_region.toLowerCase().includes(this.criteria.toLowerCase()))
			                });
        				},

        				filter: () => {
        					this.rules['projects']['search']();

			                this.projects.filtered = this.projects.filtered.filter(project => {
			                    return (
			                    		(! this.filters.showCompletedProjects && project.status != 'Complete')
			                    			|| (this.filters.showCompletedProjects)
			                    	   ) && (this.selectedClientId == '' || project.client_id == this.selectedClientId);
			                });
						},

						sortOptions: [
							{
								key: 'name',
								text: 'Name'
							},
							{
								key: 'project_type',
								text: 'Project Type'
							},
							{
								key: 'priority',
								text: 'Priority'
							},
							{
								key: 'status',
								text: 'Status'
							},
							{
								key: 'stage',
								text: 'Stage'
							},
							{
								key: 'property_type',
								text: 'Property Type'
							},
							{
								key: 'property_usage',
								text: 'Property Usage'
							},
							{
								key: 'property_region',
								text: 'Property Region'
							},
						]
		            },

        			projectSites: {
        				search: async () => {
			                this.projectSites.filtered = this.projectSites.filtered.filter(site => {
			                    return (
			                    		site.building.address_line_1.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.city.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.state.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  site.building.postal_code.toLowerCase().includes(this.criteria.toLowerCase()))
			                });
        				},

        				filter: () => {
        					this.rules['projectSites']['search']();

			                this.projectSites.filtered = this.projectSites.filtered.filter(site => {
			                    return site.client.name.toLowerCase().includes(this.criteria.toLowerCase())
			                });
						},

						sortOptions: [
							{
								key: 'building.address_line_1',
								text: 'Address'
							},
							{
								key: 'lease_end_date',
								text: 'Lease End Date'
							},
							{
								key: 'utilization',
								text: 'Utilization'
							},
						]
		            },

        			projectComps: {
        				search: async () => {
			                this.projectComps.filtered = this.projectComps.all.filter(comp => {
			                    return (
			                    		comp.address_line_1.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  comp.city.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  comp.state.toLowerCase().includes(this.criteria.toLowerCase())
			                        ||  comp.postal_code.toLowerCase().includes(this.criteria.toLowerCase()))
			                });
        				},

        				filter: () => {
        					this.rules['projectComps']['search']();

			                this.projectComps.filtered = this.projectComps.filtered.filter(comp => {
			                    return  ( this.filters.availableSqFtFilter == '' ||
			                   			  comp.available_square_footage >= parseInt(this.filters.availableSqFtFilter)
										)
			                   		&& (! this.filters.forSaleFilter || (this.filters.forSaleFilter && comp.for_sale))
			                });
						},

						sortOptions: [
							{
								key: 'pivot.favorited_at, pivot.score',
								text: 'Favorited'
							},
							{
								key: 'address_line_1',
								text: 'Address'
							},
							{
								key: 'total_square_footage',
								text: 'Square Feet'
							},
							{
								key: 'available_square_footage',
								text: 'Available Square Feet'
							},
						]
		            },

		            documents: {
        				search: async () => {
			                this.documents.filtered = this.documents.filtered.filter(file => {
			                    return ((file.name.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (file.description != null && file.description.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (file.category_name != null && file.category_name.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (file.subcategory_name != null && file.subcategory_name.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (file.uploaded_by_name.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (file.uploaded_at.toLowerCase().includes(this.criteria.toLowerCase())))
			                });
        				},

		            	filter: () => {
		            		this.rules['documents']['search']();

			                this.documents.filtered = this.documents.filtered.filter(file => {
			                    return (this.filters.category_id == '' || file.category_id == this.filters.category_id)
			                        && (this.filters.subcategory_id == '' || file.subcategory_id == this.filters.subcategory_id)
			                });
						},
					},

		            reports: {
        				search: async () => {
			                this.reports.filtered = this.reports.all.filter(report => {
			                    return ((report.name.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (report.description != null && report.description.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (report.category_name != null && report.category_name.toLowerCase().includes(this.criteria.toLowerCase())))
			                });
        				},

		            	filter: () => {
        					this.rules['reports']['search']();

			                this.reports.filtered = this.reports.filtered.filter(report => {
			                    return (this.filters.category_id == '' || report.category_id == this.filters.category_id)
			                });
						},

						sortOptions: [
							{
								key: 'category_name',
								text: 'Category'
							},
						]
		            },

		            contacts: {
        				search: async () => {
			                this.contacts.filtered = this.contacts.all.filter(contact => {
			                    return ((contact.name.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (contact.type != null && contact.type.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (contact.title != null && contact.title.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (contact.email != null && contact.email.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (contact.cell != null && contact.cell.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (contact.phone != null && contact.phone.toLowerCase().includes(this.criteria.toLowerCase()))
			                        || (contact.extension != null && contact.extension.toLowerCase().includes(this.criteria.toLowerCase())))
			                });
        				},

		            	filter: () => {
			                this.rules['contacts']['search']();
						},

						sortOptions: [
							{
								key: 'name',
								text: 'Name'
							},
							{
								key: 'title',
								text: 'Title'
							},
							{
								key: 'company.name',
								text: 'Company'
							},
						]
		            },

		            changeLog: {
        				search: async () => {
			                this.changeLog.filtered = this.changeLog.all.filter((row) => {
			                	return (_.map(_.keys(row.new_values), (field) => {
			                				return field.toLowerCase().includes(this.criteria.toLowerCase())
			                		   }).includes(true))
			                		|| row.created_at.includes(this.criteria.toLowerCase())
			                		|| moment(row.created_at).format('MM/DD/YYYY').includes(this.criteria.toLowerCase())
			                		|| row.user.name.toLowerCase().includes(this.criteria.toLowerCase());
			                });
        				},

		            	filter: () => {
        					return this.changeLog.filtered = this.changeLog.all;
						},

						sortOptions: [
							{
								key: 'created_at',
								text: 'Date'
							},

							{
								key: 'user.name',
								text: 'Changed By'
							},
						]
		            },
        		}
        	}
        },

		methods: {
            setView(type) {
            	this.layout = type;

                localStorage.setItem(`reopt_${this.dataType}_layout`, type);

                this.$emit('view-change', type);
            },

			async search() {
				this.loading = true;

				if (this.criteria == '') {
					this.filter();
				}

				await this.rules[this.dataType]['search']().then(() => {
	        		this.sort();
				});
			},

			filter() {
				if (!this[this.dataType] || !this[this.dataType].all) {
					return;
				}
				this[this.dataType].filtered = this[this.dataType].all;
				this.rules[this.dataType]['filter']()
				this.sort();
			},

            sort() {
                this[this.dataType].filtered = _.orderBy(this[this.dataType].filtered, this.sortDataBy, (this.sortDirection ? 'asc' : 'desc'));
            },

            clearSearch() {
                this.criteria = '';
            },

			resetSearch() {
				this.clearSearch();

				this.$refs.search.value = '';

				this.$nextTick(() => {
					this.$emit('searching', false);
					this.$eventHub.$emit('reset-search');
				});
			},

            debounce: _.debounce(function (criteria) {
            	this.loading = true;
        		this.criteria = criteria.target.value;
        	}, 400)
		}
	}
</script>